<template>
  <div class="information">
    <div>
      <p class="details">
        Proporciona un nivel adicional de seguridad a los inicios de sesión de tus usuarios a través de una autenticación multifactor (MFA).
      </p>

      <p class="subtitle">
        Elige cuál será la autenticación en 2 pasos (2FA):
      </p>

      <a-row :gutter="[16, 16]">
        <a-col :span="24" v-for="(option, key) in mfaTypes" :key="key">
          <a-card class="option-card">
            <template #title>
              <a-row align="middle" justify="space-between">
                <a-col>
                  <div class="option-header">
                    <img :src="option.icon" alt="phone-icon" />
                    <span class="option-title">{{ option.title }}</span>
                    <a-tag color="blue" class="ml-2" v-if="option.isRecomended">Recomendado</a-tag>
                  </div>
                  <div class="option-description">
                    {{ option.description }}
                  </div>
                </a-col>
                <a-col>
                  <a-tooltip>
                    <template #title v-if="typeMfa && typeMfa != key">
                      Solo se permite una autenticación activada a la vez. Desactiva la actual para cambiar.
                    </template>
                    <a-switch
                      :checked="typeMfa == key"
                      :disabled="typeMfa && typeMfa != key"
                      @change="selectOption(key)"
                    />
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <div class="option_actions" v-if="key == typeMfa">
              <span class="option_actions--details">
                Autenticación activada el {{ actualConfigMfa?.activated_at }}
              </span>
              <a-button type="link" @click="editConfig(key)">
                Editar
                <EditOutlined />
              </a-button>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>
  </div>

  <TwoFARoleSelector
    v-model:visible="showModal"
    :config="actualConfigMfa"
    :isEdit="isEdit"
    :mfa-type="selectedOption"
    @enableConfig="handleSelectOption"
    @refresh="handleRefreshMfa"
  />

  <TwoFARoleInstructions
    v-model:visible="showModalInstructions"
    :mfa-type="selectedOption"
    @next="showModal = true"
  />
</template>

<script setup lang="ts">
import { createVNode, onMounted, ref, nextTick } from "vue";
import phoneIcon from "@/app/settingsMenu/assets/phone-icon.svg?url";
import mailIcon from "@/app/settingsMenu/assets/mail-icon.svg?url";
import TwoFARoleSelector from "@/app/settingsMenu/components/securityPermits/form/TwoFARoleSelector.vue";
import { MfaType } from "@/app/auth/utils/enums";
import { EditOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { mfaConfigService } from "../../services/MfaConfigService";
import { dateToString } from "@/app/shared/utils/dates";
import { humanDateWithSlashFormat } from "@/app/shared/utils/constants";
import { message, Modal } from "ant-design-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";
import TwoFARoleInstructions from "./form/TwoFARoleInstructions.vue";

const showModal = ref(false);
const showModalInstructions = ref(false);
const selectedOption = ref<MfaType | null>(null);
const typeMfa = ref<MfaType | null>(null);
const actualConfigMfa = ref<any>();
const isEdit = ref(false);

const mfaTypes = {
  [MfaType.Google]: {
    icon: phoneIcon,
    title: 'Google Authenticator',
    description: 'Genera códigos de verificación dinámicos mediante Google Authenticator.',
    isRecomended: true,
    inactiveTitle: '¿Deseas inactivar la autenticación en 2 pasos por Google Authenticator?',
  },
  [MfaType.Email]: {
    icon: mailIcon,
    title: 'Correo electrónico',
    description: 'Genera y envía códigos de verificación mediante tu correo electrónico.',
    isRecomended: false,
    inactiveTitle: '¿Deseas inactivar la autenticación en 2 pasos por correo electrónico?',
  },
};

const selectOption = (option: MfaType) => {
  // selectedOption.value = null;
  isEdit.value = false;
  console.log(selectedOption.value, '-', option);
  if (typeMfa.value == option) {
    Modal.confirm({
      centered: true,
      icon: createVNode(QuestionCircleOutlined),
      title: mfaTypes[typeMfa.value ?? MfaType.Google].inactiveTitle,
      content: "Al inactivar, no se guardarán las configuraciones previas de rol y tiempo de expiración.",
      okText: 'Sí, inactivar',
      cancelText: 'No, cancelar',
      onOk: async () => {
        try {
          await mfaConfigService.disabledMfa({ mfa_type: option });
          selectedOption.value = null;
          typeMfa.value = null;
          await handleRefreshMfa();
          message.success("Se deshabilitó la configuración correctamente")
        } catch (error) {
          const messageError =
            error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

          message.error(messageError);
        }
      }
    });
    return;
  }

  selectedOption.value = option;
  if (option == MfaType.Google || option == MfaType.Email) {
    showModalInstructions.value = true;
    // showModal.value = true;
  }
};

const editConfig = async (option: MfaType) => {
  isEdit.value = true;
  selectedOption.value = option;
  await nextTick();
  showModal.value = true;
}

const handleSelectOption = () => {
  typeMfa.value = selectedOption.value;
}

const handleRefreshMfa = async () => {
  const response = await mfaConfigService.listConfigMfa();
  console.log(response, 'response listConfigMfa');
  const configsEnabled = response.results.filter((el: any) => el.status == 1);
  const count = configsEnabled.length;
  if (count > 0) {
    const config = configsEnabled?.[count - 1];
    const time_to_expire = config.time_to_expire;
    const hours = Math.floor(time_to_expire / 3600);
    const minutes = Math.floor((time_to_expire % 3600) / 60);
    typeMfa.value = config.mfa_type;

    actualConfigMfa.value = {
      role: config.role,
      mfa_type: config.mfa_type,
      hours,
      minutes,
      activated_at: dateToString(config.updated_at, humanDateWithSlashFormat)
    };
  }
}

onMounted(async () => {
  await handleRefreshMfa(); 
});
</script>

<style lang="sass" scoped>
.text__strong
  font-weight: 600
  font-size: 16px

.flex
  display: flex

.input
  display: flex
  align-items: center
  gap: 40px
  margin-bottom: 12px

.details
  margin-bottom: 0

.disabled
  opacity: 0.30

.subtitle
  font-weight: 600
  margin-top: 10px

.auth-option
  cursor: pointer
  border-radius: 12px
  transition: border 0.3s, box-shadow 0.3s

.auth-option.selected
  border-color: #1890ff !important
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2)

.option
  &-card
    :deep(.ant-card-body)
      padding: 8px 8px 8px 18px !important
    :deep(.ant-card-head)
      padding: 16px !important
  &-header
    display: flex
    align-items: center
    font-size: 16px
    gap: 10px
    font-weight: 400
    .option-title
      font-weight: 600

  &-description
    font-size: 14px
    color: #888
    margin-top: 4px
    margin-left: 30px
    font-weight: 400

  &_actions
    width: 100%
    display: flex
    justify-content: space-between
    align-items: center
    &--details
      font-style: italic
      color: #888

.ml-2
  margin-left: 8px

</style>

<style></style>
